export class Bot {
    constructor(x, y, name, color = '#00ff88', shipStyle = 'dart', secondaryColor = null) {
        this.x = x;
        this.y = y;
        this.size = 18;
        this.speed = 2.5;
        this.maxHp = 80;
        this.hp = 80;
        this.damage = 8;
        this.name = name;
        this.color = color;
        this.shipStyle = shipStyle;
        this.secondaryColor = secondaryColor || color;
        this.vx = 0;
        this.vy = 0;
        this.angle = -Math.PI / 2;
        this.shootCooldown = 0;
        this.maxShootCooldown = 25;
        this.target = null;
        this.isDead = false;
        this.deathTime = 0;
        this.respawnDelay = 120000; // 2 minutes
        this.aiState = 'roam';
        this.roamTarget = null;
        this.roamCooldown = 0;
    }

    /** Restyle for current planet faction */
    applyFactionTheme(faction, colorOverride = null) {
        if (!faction) return;
        this.shipStyle = faction.shipStyle || 'dart';
        this.secondaryColor = faction.playerSecondary || faction.playerPrimary || this.color;
        if (colorOverride) this.color = colorOverride;
        else if (faction.botPalette?.length) {
            this.color = faction.botPalette[Math.floor(Math.random() * faction.botPalette.length)];
        }
    }

    update(canvas, enemies, player) {
        if (this.isDead) return;

        // Update shoot cooldown
        if (this.shootCooldown > 0) {
            this.shootCooldown--;
        }

        // AI behavior
        this.updateAI(canvas, enemies, player);

        // Apply movement
        this.x += this.vx;
        this.y += this.vy;

        // Friction
        this.vx *= 0.9;
        this.vy *= 0.9;

        // Keep in bounds
        this.x = Math.max(this.size, Math.min(canvas.width - this.size, this.x));
        this.y = Math.max(this.size, Math.min(canvas.height - this.size, this.y));
    }

    updateAI(canvas, enemies, player) {
        // Find nearest enemy
        let nearest = null;
        let minDist = Infinity;

        enemies.forEach(enemy => {
            const dx = enemy.x - this.x;
            const dy = enemy.y - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < minDist) {
                minDist = dist;
                nearest = enemy;
            }
        });

        if (nearest && minDist < 400) {
            // Combat mode - move toward enemy but keep distance
            this.aiState = 'combat';
            this.target = nearest;

            const dx = nearest.x - this.x;
            const dy = nearest.y - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            const optimalDistance = 150;

            if (dist > optimalDistance) {
                // Move closer
                this.vx = (dx / dist) * this.speed;
                this.vy = (dy / dist) * this.speed;
            } else if (dist < optimalDistance - 50) {
                // Move away
                this.vx = -(dx / dist) * this.speed;
                this.vy = -(dy / dist) * this.speed;
            } else {
                // Strafe
                this.vx = -(dy / dist) * this.speed * 0.5;
                this.vy = (dx / dist) * this.speed * 0.5;
            }
        } else {
            // Roam mode - follow player loosely
            this.aiState = 'roam';

            const dx = player.x - this.x;
            const dy = player.y - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist > 100) {
                this.vx = (dx / dist) * this.speed * 0.5;
                this.vy = (dy / dist) * this.speed * 0.5;
            }
        }
    }

    canShoot() {
        return this.shootCooldown === 0 && this.target && !this.isDead;
    }

    shoot() {
        if (!this.canShoot()) return null;

        const dx = this.target.x - this.x;
        const dy = this.target.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        this.shootCooldown = this.maxShootCooldown;

        return {
            x: this.x,
            y: this.y,
            vx: (dx / dist) * 7,
            vy: (dy / dist) * 7,
            damage: this.damage,
            color: this.color,
            isBotProjectile: true
        };
    }

    takeDamage(damage) {
        this.hp -= damage;
        if (this.hp <= 0 && !this.isDead) {
            this.die();
            return true;
        }
        return false;
    }

    die() {
        this.isDead = true;
        this.deathTime = Date.now();
    }

    canRespawn() {
        return this.isDead && Date.now() - this.deathTime >= this.respawnDelay;
    }

    respawn(x, y) {
        this.isDead = false;
        this.hp = this.maxHp;
        this.x = x;
        this.y = y;
        this.vx = 0;
        this.vy = 0;
        this.target = null;
    }

    draw(ctx) {
        if (this.isDead) return;

        ctx.save();
        const s = this.size;
        const color = this.color;
        const sec = this.secondaryColor || color;

        // Soft glow
        const glow = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, s * 2.2);
        glow.addColorStop(0, color + '55');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(this.x, this.y, s * 2.2, 0, Math.PI * 2);
        ctx.fill();

        // Face movement (or last angle)
        if (Math.hypot(this.vx, this.vy) > 0.2) {
            this.angle = Math.atan2(this.vy, this.vx);
        }
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle + Math.PI / 2);
        ctx.shadowBlur = 14;
        ctx.shadowColor = color;
        this._drawHull(ctx, s, color, sec);
        ctx.restore();

        // HP bar
        if (this.hp < this.maxHp) {
            const barWidth = s * 2;
            const barHeight = 3;
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(this.x - barWidth / 2, this.y - s - 15, barWidth, barHeight);
            ctx.fillStyle = '#33ff88';
            ctx.fillRect(this.x - barWidth / 2, this.y - s - 15, barWidth * (this.hp / this.maxHp), barHeight);
        }

        // Name tag
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.font = 'bold 11px Rajdhani, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const tw = ctx.measureText(this.name).width;
        ctx.fillStyle = 'rgba(6,10,28,0.7)';
        ctx.strokeStyle = color + '88';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(this.x - tw / 2 - 4, this.y - s - 32, tw + 8, 14, 4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(this.name, this.x, this.y - s - 20);

        ctx.restore();
    }

    /** Compact neon hull matching planet shipStyle */
    _drawHull(ctx, s, color, sec) {
        const style = this.shipStyle || 'dart';
        ctx.globalAlpha = 0.95;
        switch (style) {
            case 'wedge':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.05);
                ctx.lineTo(s * 0.95, s * 0.7);
                ctx.lineTo(0, s * 0.15);
                ctx.lineTo(-s * 0.95, s * 0.7);
                ctx.closePath();
                ctx.fill();
                break;
            case 'diamond':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.15);
                ctx.lineTo(s * 0.6, 0);
                ctx.lineTo(0, s * 0.95);
                ctx.lineTo(-s * 0.6, 0);
                ctx.closePath();
                ctx.fill();
                break;
            case 'blade':
            case 'bolt':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.2);
                ctx.lineTo(s * 0.35, s * 0.2);
                ctx.lineTo(s * 0.15, s * 0.8);
                ctx.lineTo(0, s * 0.4);
                ctx.lineTo(-s * 0.15, s * 0.8);
                ctx.lineTo(-s * 0.35, s * 0.2);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = sec;
                ctx.beginPath();
                ctx.moveTo(s * 0.2, 0);
                ctx.lineTo(s * 0.95, s * 0.5);
                ctx.lineTo(s * 0.15, s * 0.3);
                ctx.closePath();
                ctx.fill();
                ctx.beginPath();
                ctx.moveTo(-s * 0.2, 0);
                ctx.lineTo(-s * 0.95, s * 0.5);
                ctx.lineTo(-s * 0.15, s * 0.3);
                ctx.closePath();
                ctx.fill();
                break;
            case 'ring':
                ctx.strokeStyle = color;
                ctx.lineWidth = 2.5;
                ctx.beginPath();
                ctx.ellipse(0, 0, s * 0.95, s * 0.45, 0, 0, Math.PI * 2);
                ctx.stroke();
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(0, 0, s * 0.5, 0, Math.PI * 2);
                ctx.fill();
                break;
            case 'spiker':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(0, 0, s * 0.45, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = sec;
                for (let i = 0; i < 5; i++) {
                    const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
                    ctx.beginPath();
                    ctx.moveTo(Math.cos(a - 0.28) * s * 0.35, Math.sin(a - 0.28) * s * 0.35);
                    ctx.lineTo(Math.cos(a) * s * 1.05, Math.sin(a) * s * 1.05);
                    ctx.lineTo(Math.cos(a + 0.28) * s * 0.35, Math.sin(a + 0.28) * s * 0.35);
                    ctx.closePath();
                    ctx.fill();
                }
                break;
            case 'fin':
            case 'leaf':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.1);
                ctx.quadraticCurveTo(s * 0.85, 0, 0, s * 0.8);
                ctx.quadraticCurveTo(-s * 0.85, 0, 0, -s * 1.1);
                ctx.fill();
                break;
            case 'prism':
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.1);
                ctx.lineTo(s * 0.55, s * 0.2);
                ctx.lineTo(s * 0.2, s * 0.8);
                ctx.lineTo(-s * 0.2, s * 0.8);
                ctx.lineTo(-s * 0.55, s * 0.2);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = sec;
                ctx.globalAlpha = 0.7;
                ctx.beginPath();
                ctx.moveTo(0, -s * 0.55);
                ctx.lineTo(s * 0.25, s * 0.1);
                ctx.lineTo(0, s * 0.4);
                ctx.lineTo(-s * 0.25, s * 0.1);
                ctx.closePath();
                ctx.fill();
                ctx.globalAlpha = 0.95;
                break;
            case 'block':
            case 'mech':
                ctx.fillStyle = color;
                ctx.fillRect(-s * 0.65, -s * 0.5, s * 1.3, s * 1.0);
                ctx.beginPath();
                ctx.moveTo(0, -s * 0.95);
                ctx.lineTo(s * 0.45, -s * 0.35);
                ctx.lineTo(-s * 0.45, -s * 0.35);
                ctx.closePath();
                ctx.fill();
                break;
            case 'dart':
            default:
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(0, -s * 1.05);
                ctx.lineTo(s * 0.7, s * 0.55);
                ctx.lineTo(0, s * 0.22);
                ctx.lineTo(-s * 0.7, s * 0.55);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = sec;
                ctx.globalAlpha = 0.7;
                ctx.beginPath();
                ctx.moveTo(s * 0.2, s * 0.05);
                ctx.lineTo(s * 0.75, s * 0.6);
                ctx.lineTo(s * 0.12, s * 0.3);
                ctx.closePath();
                ctx.fill();
                ctx.beginPath();
                ctx.moveTo(-s * 0.2, s * 0.05);
                ctx.lineTo(-s * 0.75, s * 0.6);
                ctx.lineTo(-s * 0.12, s * 0.3);
                ctx.closePath();
                ctx.fill();
                ctx.globalAlpha = 0.95;
                break;
        }
        // Cockpit glint
        ctx.fillStyle = '#ffffffcc';
        ctx.beginPath();
        ctx.ellipse(0, -s * 0.15, s * 0.12, s * 0.16, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
    }

    static generateBotNames() {
        const names = [
            'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon',
            'Zeta', 'Theta', 'Lambda', 'Omega', 'Sigma',
            'Phoenix', 'Dragon', 'Tiger', 'Wolf', 'Eagle',
            'Shadow', 'Ghost', 'Phantom', 'Spectre', 'Wraith'
        ];
        return names[Math.floor(Math.random() * names.length)];
    }

    static generateBotColor(faction = null) {
        if (faction?.botPalette?.length) {
            return faction.botPalette[Math.floor(Math.random() * faction.botPalette.length)];
        }
        const colors = [
            '#00ff88', '#ff8800', '#8800ff',
            '#00ffff', '#ff00ff', '#ffff00'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    }
}
